import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import PqrSectionModal from '../pqrPreview/PqrSectionModal'
import SingleSelect from '../../ui/SingleSelect'
import Button from '../../ui/Button'
import SignatureDialog from '../../ui/SignatureDialog'
import type { TeamMember } from '../../../types/teamMember'
import { resolveMemberUuidForStoredPerson } from '../../../utils/teamMemberLookup'

/** Canonical slots plus any extra imported / custom roles (e.g. inspector, sig-4). */
export type WpsApprovalId = string

export interface WpsApprovalDraft {
  id: WpsApprovalId
  label: string
  name: string
  title?: string
  date?: string
  signature?: string
  stampImage?: string
  signedAt?: string
  memberId?: string
}

interface Props {
  open: boolean
  initial: WpsApprovalDraft[]
  members: TeamMember[]
  teamMembersHref: string
  onApply: (next: WpsApprovalDraft[]) => void
  onCancel: () => void
}

const DEFAULT_SLOTS: WpsApprovalDraft[] = [
  { id: 'contractor', label: 'Contractor', name: '' },
  { id: 'endClient', label: 'Client', name: '' },
  { id: 'crc', label: 'CRC Evans', name: '' }
]

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function withAutoDate(entry: WpsApprovalDraft): WpsApprovalDraft {
  if (entry.date?.trim()) return entry
  if (!entry.name.trim()) return entry
  return { ...entry, date: todayIsoDate() }
}

function normalizeInitial(initial: WpsApprovalDraft[]): WpsApprovalDraft[] {
  const byId = new Map(initial.map((e) => [e.id, e]))
  const ordered: WpsApprovalDraft[] = DEFAULT_SLOTS.map((slot) => {
    const found = byId.get(slot.id)
    return found ? { ...slot, ...found, label: found.label || slot.label } : { ...slot }
  })
  for (const entry of initial) {
    if (!DEFAULT_SLOTS.some((s) => s.id === entry.id)) {
      ordered.push({
        ...entry,
        label: entry.label || entry.title || entry.id
      })
    }
  }
  return ordered.map(withAutoDate)
}

function defaultLabelForId(id: string): string {
  if (id === 'contractor') return 'Contractor'
  if (id === 'endClient') return 'Client'
  if (id === 'crc') return 'CRC Evans'
  if (id.startsWith('sig-')) return `Signature ${id.slice(4)}`
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function WpsApprovalsModal({
  open,
  initial,
  members,
  teamMembersHref,
  onApply,
  onCancel
}: Props) {
  const [draft, setDraft] = useState<WpsApprovalDraft[]>(() => normalizeInitial(initial))
  const [signatureColumnId, setSignatureColumnId] = useState<WpsApprovalId | null>(null)

  useEffect(() => {
    if (!open) return
    setDraft(normalizeInitial(initial))
  }, [open, JSON.stringify(initial)]) // eslint-disable-line react-hooks/exhaustive-deps

  const memberById = useMemo(() => {
    const map = new Map<string, TeamMember>()
    members.forEach((m) => map.set(m.id, m))
    return map
  }, [members])

  const memberOptions = useMemo(() => members.map((m) => m.id), [members])
  const noMembers = members.length === 0

  const isDirty = JSON.stringify(draft) !== JSON.stringify(normalizeInitial(initial))

  const byId = (id: WpsApprovalId): WpsApprovalDraft => {
    const found = draft.find((d) => d.id === id)
    if (found) return found
    return {
      id,
      label: defaultLabelForId(id),
      name: ''
    }
  }

  const updateEntry = (id: WpsApprovalId, patch: Partial<WpsApprovalDraft>) => {
    setDraft((current) => {
      const exists = current.find((d) => d.id === id)
      if (exists) {
        return current.map((d) => (d.id === id ? { ...d, ...patch } : d))
      }
      return [...current, { ...byId(id), ...patch }]
    })
  }

  const handleSelectMember = (id: WpsApprovalId) => (uuid: string) => {
    const member = memberById.get(uuid)
    if (!member) return
    const current = byId(id)
    updateEntry(id, {
      memberId: member.id,
      name: member.name,
      date: current.date?.trim() ? current.date : todayIsoDate()
    })
  }

  const addSlot = () => {
    setDraft((current) => {
      let n = current.length + 1
      let id = `sig-${n}`
      while (current.some((d) => d.id === id)) {
        n += 1
        id = `sig-${n}`
      }
      return [
        ...current,
        { id, label: `Additional signer ${n}`, name: '' }
      ]
    })
  }

  const removeSlot = (id: WpsApprovalId) => {
    if (DEFAULT_SLOTS.some((s) => s.id === id)) return
    setDraft((current) => current.filter((d) => d.id !== id))
  }

  const activeEntry = signatureColumnId ? byId(signatureColumnId) : null

  const handleSaveSignature = (
    signature: string,
    name: string,
    _comment?: string,
    stampImage?: string
  ) => {
    if (!signatureColumnId) return
    const current = byId(signatureColumnId)
    updateEntry(signatureColumnId, {
      name: name || current.name,
      signature,
      stampImage,
      signedAt: new Date().toISOString(),
      date: current.date?.trim() ? current.date : todayIsoDate()
    })
    setSignatureColumnId(null)
  }

  return (
    <>
      <PqrSectionModal
        open={open}
        title="Edit WPS approvals"
        isModalDirty={isDirty}
        onApply={() =>
          onApply(
            draft.map((entry) => ({
              id: entry.id,
              label: entry.label,
              name: entry.name,
              title: entry.title && entry.title.trim() ? entry.title : undefined,
              date: entry.date && entry.date.trim() ? entry.date : undefined,
              signature: entry.signature,
              stampImage: entry.stampImage,
              signedAt: entry.signedAt,
              memberId: entry.memberId
            }))
          )
        }
        onCancel={onCancel}
        panelMaxWidthClassName="max-w-6xl"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {draft.map((entry) => {
            const id = entry.id
            const memberUuid = resolveMemberUuidForStoredPerson(
              { id: '', name: entry.name, memberId: entry.memberId },
              members
            )
            const renderTriggerLabel = (uuid: string): string => {
              const m = memberById.get(uuid)
              return m ? `${m.name} (${m.memberExternalId})` : uuid
            }
            const isDefault = DEFAULT_SLOTS.some((s) => s.id === id)
            return (
              <div key={id} className="rounded-md border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {entry.label}
                  </p>
                  {!isDefault ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="text-xs"
                      onClick={() => removeSlot(id)}
                      data-testid={`wps-approval-remove-${id}`}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
                <label className="text-xs">
                  <span className="mb-1 block font-medium text-gray-700">Role / label</span>
                  <input
                    type="text"
                    value={entry.label}
                    onChange={(e) => updateEntry(id, { label: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
                    data-testid={`wps-approval-label-${id}`}
                  />
                </label>
                <label className="mt-2 text-xs">
                  <span className="mb-1 block font-medium text-gray-700">Team member</span>
                  <SingleSelect
                    value={memberUuid}
                    options={memberOptions}
                    placeholder={noMembers ? 'No active team members' : 'Select member…'}
                    searchPlaceholder="Search team members…"
                    interactionLocked={noMembers}
                    lockedHint={noMembers ? 'No active team members for this project.' : undefined}
                    getOptionLabel={renderTriggerLabel}
                    getTriggerLabel={renderTriggerLabel}
                    onChange={handleSelectMember(id)}
                  />
                </label>
                <label className="mt-2 block text-xs">
                  <span className="mb-1 block font-medium text-gray-700">Name</span>
                  <input
                    type="text"
                    value={entry.name}
                    onChange={(e) => updateEntry(id, { name: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
                    data-testid={`wps-approval-name-${id}`}
                  />
                </label>
                <label className="mt-2 block text-xs">
                  <span className="mb-1 block font-medium text-gray-700">Title</span>
                  <input
                    type="text"
                    value={entry.title ?? ''}
                    onChange={(e) => updateEntry(id, { title: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
                    data-testid={`wps-approval-title-${id}`}
                  />
                </label>
                {entry.date?.trim() ? (
                  <p className="mt-2 text-xs text-gray-600" data-testid={`wps-approval-date-${id}`}>
                    Date: {entry.date}
                  </p>
                ) : null}
                <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                  {entry.signature ? (
                    <img
                      src={entry.signature}
                      alt={`${entry.label} signature`}
                      className="mx-auto max-h-16 max-w-full"
                    />
                  ) : (
                    <p className="text-center text-xs text-gray-400">No signature yet</p>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1 text-xs"
                    disabled={!entry.name.trim()}
                    onClick={() => setSignatureColumnId(id)}
                  >
                    Add Signature
                  </Button>
                  {entry.signature ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="text-xs"
                      onClick={() =>
                        updateEntry(id, {
                          signature: undefined,
                          stampImage: undefined,
                          signedAt: undefined
                        })
                      }
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" className="text-xs" onClick={addSlot} data-testid="wps-approval-add-slot">
            Add signer
          </Button>
          {noMembers ? (
            <p className="text-xs text-hero-red-70">
              No active team members found.{' '}
              <Link to={teamMembersHref} className="font-medium text-brand-red-600 underline">
                Add a team member
              </Link>
              .
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              Don&apos;t see who you need?{' '}
              <Link to={teamMembersHref} className="font-medium text-brand-red-600 underline">
                Manage team members
              </Link>
              .
            </p>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Approvals are staged in the preview. Click <strong>Save</strong> or{' '}
          <strong>Save &amp; Generate Report</strong> on the page header to persist them.
        </p>
      </PqrSectionModal>

      <SignatureDialog
        isOpen={signatureColumnId !== null}
        onClose={() => setSignatureColumnId(null)}
        onSave={handleSaveSignature}
        title={activeEntry ? `${activeEntry.label} Signature` : 'Add Signature'}
        initialName={activeEntry?.name ?? ''}
        initialSignature={activeEntry?.signature ?? null}
        initialStampImage={activeEntry?.stampImage ?? null}
      />
    </>
  )
}
